import axios, { AxiosInstance, AxiosResponse, AxiosError, InternalAxiosRequestConfig } from 'axios'

import {
    ErrorResponse,
    InternalServerError,
    NotFoundError,
    UnauthorizedError,
    ValidationError
} from '@/models/Exceptions'

import { ValidationNetCoreError } from '@/models/ValidationNetCoreError'
import { ApiExceptionMiddleware } from '@/models/ApiExceptionMiddleware'

const onRequest = (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
    return config
}

const onRequestError = (error: AxiosError): Promise<AxiosError> => Promise.reject(error)

const onResponse = (response: AxiosResponse): AxiosResponse => response

const onResponseError = (error: AxiosError): Promise<AxiosError> => {
    const status = error.response?.status
    const isValidationNetCoreResponse = (response: unknown): response is ValidationNetCoreError => response !== undefined && (response as Partial<ValidationNetCoreError>).errors !== undefined
    const isApiExceptionMiddlewareResponse = (response: unknown): response is ApiExceptionMiddleware => response !== undefined && (response as Partial<ApiExceptionMiddleware>).Message !== undefined

    if (status === 400) {
        const response = error.response as AxiosResponse<ValidationNetCoreError>
        if (isValidationNetCoreResponse(response.data)) return Promise.reject(new ValidationError(response.data.errors, status))
    }

    let message
    if (isApiExceptionMiddlewareResponse(error.response?.data)) {
        const response = error.response as AxiosResponse<ApiExceptionMiddleware>
        message = response.data.Message
    } else {
        const response = error.response as AxiosResponse<string>
        message = response.data
    }

    switch (status) {
        case 401:
            return Promise.reject(new UnauthorizedError(message, status))
        case 403:
            return Promise.reject(new UnauthorizedError(message, status))
        case 404:
            return Promise.reject(new NotFoundError(message, status))
        case 500:
            return Promise.reject(new InternalServerError(message, status))
        default:
            return Promise.reject(new ErrorResponse(message, status))
    }
}

const instance: AxiosInstance = axios.create({
    baseURL: `${process.env.VUE_APP_API as string}/ivp`,
    headers: {
        'X-Requested-With': 'XMLHttpRequest'
    }
})

instance.interceptors.request.use(onRequest, onRequestError)
instance.interceptors.response.use(onResponse, onResponseError)

export const useAxios = (): AxiosInstance => instance
